<script setup>
import { nextTick, onBeforeUnmount, watch, ref } from 'vue';

defineProps({
  onClose: { type: Function, default: () => {} },
  closeOnBackdrop: { type: Boolean, default: true },
});

const show = defineModel('show', { type: Boolean, default: false });
const dialogRef = ref(null);

const lockScroll = locked => {
  const el = document?.documentElement;
  if (!el) return;
  if (locked) el.style.overflow = 'hidden';
  else el.style.overflow = '';
};

const handleClose = () => {
  show.value = false;
};

const onKeydown = e => {
  if (e.key === 'Escape') handleClose();
};

watch(
  show,
  async next => {
    lockScroll(next);
    if (next) {
      window.addEventListener('keydown', onKeydown);
      await nextTick();
      dialogRef.value?.focus?.();
    } else {
      window.removeEventListener('keydown', onKeydown);
    }
  },
  { immediate: true }
);

onBeforeUnmount(() => {
  lockScroll(false);
  window.removeEventListener('keydown', onKeydown);
});
</script>

<template>
  <teleport to="body">
    <transition name="woot-modal-fade">
      <div
        v-if="show"
        class="modal-mask"
        @click.self="closeOnBackdrop && handleClose()"
      >
        <div
          ref="dialogRef"
          class="modal-container"
          role="dialog"
          aria-modal="true"
          tabindex="-1"
        >
          <slot />
        </div>
      </div>
    </transition>
  </teleport>
</template>

<style scoped>
.modal-mask {
  position: fixed;
  inset: 0;
  z-index: 9999;
  background: rgba(15, 23, 42, 0.36);
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 20px;
}

.modal-container {
  width: min(720px, 100%);
  max-height: min(84vh, 900px);
  overflow: auto;
  background: #ffffff;
  border: 1px solid rgba(15, 23, 42, 0.12);
  border-radius: 12px;
  box-shadow: 0 18px 60px rgba(15, 23, 42, 0.25);
  outline: none;
}

.woot-modal-fade-enter-active,
.woot-modal-fade-leave-active {
  transition: opacity 0.18s ease;
}

.woot-modal-fade-enter-from,
.woot-modal-fade-leave-to {
  opacity: 0;
}
</style>
